import isEqual from 'lodash.isequal';
import PropTypes from 'prop-types';
import React, { forwardRef, useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';
import { hexToRGBA } from '_helpers/theme';
import { usePrevious } from '_hooks';

import Button from 'components/atomic/Button';
import Icon from 'components/atomic/Icon';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import DateField from 'components/atomic/fields/DateField';
import MaskField from 'components/atomic/fields/MaskField';
import SelectField from 'components/atomic/fields/SelectField';
import Text from 'components/atomic/layouts/Text';

const initialFields = {
  [FIELD_KEYS.relationship]: '',
  [FIELD_KEYS.firstName]: '',
  [FIELD_KEYS.lastName]: '',
  [FIELD_KEYS.ssn]: '',
  [FIELD_KEYS.dateOfBirth]: new Date(),
  [FIELD_KEYS.email]: '',
  [FIELD_KEYS.phone]: '',
  [FIELD_KEYS.address1]: '',
  [FIELD_KEYS.zip]: '',
  [FIELD_KEYS.city]: '',
  [FIELD_KEYS.state]: '',
};

// eslint-disable-next-line react/display-name, react/prop-types, no-unused-vars
const StyledForm = styled(forwardRef(({ empty, ...props }, ref) => <Form ref={ref} {...props} />))`
  width: 100%;
  flex-grow: 1;

  ${({ empty }) =>
    empty &&
    css`
      display: flex;
      flex-direction: column;
      gap: ${theme.spaces.large}px;
    `}
`;

const StyledEmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  background: ${hexToRGBA(theme.colors.gray.secondary, 0.04)};
  border-radius: 16px;
`;

const StyledInputRow = styled(Row)`
  width: 100%;
  padding-top: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.regular}px;
  border-bottom: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
`;

const StyledRow = styled(Row)`
  width: 100%;
  padding-top: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.regular}px;
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const DependentsForm = ({ loading, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    reset,
    setValue,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const prevDefaultValues = usePrevious(defaultValues);
  const values = useWatch({ control });
  const prevValues = usePrevious(values);
  const [emailVerifyingCounter, setEmailVerifyingCounter] = useState(0);

  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_KEYS.dependents,
  });

  const handleEmailVerifying = (flag) => {
    if (flag) {
      setEmailVerifyingCounter((prev) => prev + 1);
    } else {
      setEmailVerifyingCounter((prev) => prev - 1);
    }
  };

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (!isEqual(prevValues, values)) {
      if (values[FIELD_KEYS.spouse] === 'no' && !values[FIELD_KEYS.dependents].length) {
        setValue(FIELD_KEYS.filingSingle, 'single');
      }
      if (values[FIELD_KEYS.spouse] === 'no' && !!values[FIELD_KEYS.dependents].length) {
        setValue(FIELD_KEYS.filingSingle, 'household');
      }
      if (values[FIELD_KEYS.spouse] === 'yes') {
        setValue(FIELD_KEYS.filingSingle, null);
      }
    }
  }, [prevValues, values]);

  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [prevDefaultValues, defaultValues]);

  useEffect(() => {
    window.scroll({ top: document.body.scrollHeight, left: 0, behavior: 'smooth' });
  }, [fields.length]);

  return (
    <StyledForm empty={!fields.length} noValidate onSubmit={handleSubmit(onSubmit)}>
      {fields.map((item, index) => (
        <StyledInputRow key={item.id}>
          <Col xs={12} md={6}>
            <SelectField
              {...getFieldProps(FIELD_KEYS.relationship)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.relationship}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6} />
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.firstName)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.firstName}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.lastName)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.lastName}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <MaskField
              {...getFieldProps(FIELD_KEYS.ssn)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.ssn}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <DateField
              {...getFieldProps(FIELD_KEYS.dateOfBirth, {
                validationOptions: {
                  values,
                },
              })}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.dateOfBirth}`}
              icon={<Icon alias={ICON_ALIASES.calendar} color={theme.colors.gray.secondary} />}
              dateFormat="MM/dd/yyyy"
              bottomSpace={theme.spaces.regular}
              control={control}
            />
          </Col>
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.email, { validationOptions: { setProcessing: handleEmailVerifying } })}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.email}`}
              loading={Boolean(emailVerifyingCounter)}
              feedback={emailVerifyingCounter ? 'Verifying...' : undefined}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <MaskField
              {...getFieldProps(FIELD_KEYS.phone)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.phone}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.address1)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.address1}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.zip)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.zip}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <BaseField
              {...getFieldProps(FIELD_KEYS.city)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.city}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={6}>
            <SelectField
              {...getFieldProps(FIELD_KEYS.state)}
              name={`${FIELD_KEYS.dependents}.${index}.${FIELD_KEYS.state}`}
              control={control}
              bottomSpace={theme.spaces.regular}
            />
          </Col>
          <Col xs={12} md={9} />
          <Col xs={12} md={3}>
            <Button disabled={submitDisabled} color={theme.colors.red.primary} onClick={() => remove(index)}>
              Remove
            </Button>
          </Col>
        </StyledInputRow>
      ))}

      {!fields.length && (
        <StyledEmptyContainer>
          <StyledRow>
            <Col xs={12}>
              <Text
                size={theme.fontSizes.small}
                color={theme.colors.gray.secondary}
                bottomSpace={theme.spaces.small}
                centered
              >
                No dependents added
              </Text>
            </Col>
            <Col xs={12} md={5} />
            <Col xs={12} md={2}>
              <Button
                disabled={submitDisabled}
                color={theme.colors.indigo.primary}
                onClick={() => append(initialFields)}
              >
                + Add
              </Button>
            </Col>
            <Col xs={12} md={5} />
          </StyledRow>
        </StyledEmptyContainer>
      )}

      {!!fields.length && (
        <>
          <StyledRow>
            <Col xs={12} md={9} />
            <Col xs={12} md={3}>
              <Button
                disabled={submitDisabled}
                color={theme.colors.indigo.primary}
                onClick={() => append(initialFields)}
              >
                + Add
              </Button>
            </Col>
          </StyledRow>
        </>
      )}

      <Row>
        <Col xs={12} md={3} />
        <Col xs={12} md={6}>
          <Button type="submit" disabled={Boolean(emailVerifyingCounter) || submitDisabled} color={theme.colors.black}>
            Save Information
          </Button>
        </Col>
        <Col xs={12} md={3} />
      </Row>

      {(loading || Boolean(emailVerifyingCounter)) && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </StyledForm>
  );
};

DependentsForm.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default DependentsForm;
